import { useMemo, useState } from "react";
import { AlertTriangle, Loader2, MessageSquare, Square } from "lucide-react";
import { computeMetrics } from "../metrics";
import {
  ChangesWarning,
  EarlyTerminationBanner,
  formatPercent,
  isCaseModified,
  MetricCard,
  METRIC_TOOLTIPS,
  ResultBadge,
  StatusIcon,
  TH,
  ViewConfigButton,
} from "./shared";
import { CaseDetailModal } from "./CaseDetailModal";
import type { BenchmarkCase, CaseRunResult, CaseStatus } from "../types";

interface RunningViewProps {
  cases: BenchmarkCase[];
  caseStatuses: Map<number, CaseStatus>;
  caseMessageCounts: Map<number, number>;
  caseSessionIds: Map<number, string>;
  results: CaseRunResult[];
  currentCaseIndex: number;
  totalCases: number;
  onCancel: () => void;
  onViewConversation: (sessionId: string) => void;
  shouldTerminateEarly: boolean;
}

export function RunningView({
  cases,
  caseStatuses,
  caseMessageCounts,
  caseSessionIds,
  results,
  currentCaseIndex,
  totalCases,
  onCancel,
  onViewConversation,
  shouldTerminateEarly,
}: RunningViewProps) {
  const [modalCaseId, setModalCaseId] = useState<number | null>(null);

  const resultsByCaseId = useMemo(
    () => new Map(results.map((r) => [r.caseId, r])),
    [results],
  );

  const liveMetrics = useMemo(() => computeMetrics(results), [results]);
  const hasCompletedResults = results.some((r) => r.status === "complete");

  const completedCount = Array.from(caseStatuses.values()).filter(
    (s) => s === "complete" || s === "failed" || s === "abandoned",
  ).length;

  const modalCase = modalCaseId !== null
    ? cases.find((c) => c.id === modalCaseId)
    : undefined;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6 text-brand-foreground">
        Evaluation Running
      </h1>

      <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        Closing or refreshing this window will lose the current evaluation run.
      </div>

      {shouldTerminateEarly && <EarlyTerminationBanner />}

      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
            <span className="text-sm text-brand-foreground">
              Running case {Math.min(currentCaseIndex + 1, totalCases)} of{" "}
              {totalCases}
            </span>
          </div>
          <div className="w-64 h-2 bg-brand-border rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-primary rounded-full transition-all duration-300"
              style={{
                width: `${totalCases > 0 ? (completedCount / totalCases) * 100 : 0}%`,
              }}
            />
          </div>
          <span className="text-xs text-brand-muted">
            {completedCount}/{totalCases} complete
          </span>
        </div>
        <button
          onClick={onCancel}
          className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition-colors"
        >
          <Square className="w-4 h-4" />
          Cancel
        </button>
      </div>

      {hasCompletedResults && !shouldTerminateEarly && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          <MetricCard label="Accuracy" value={formatPercent(liveMetrics.accuracy)} tooltip={METRIC_TOOLTIPS.accuracy} />
          <MetricCard label="Precision" value={formatPercent(liveMetrics.precision)} tooltip={METRIC_TOOLTIPS.precision} />
          <MetricCard label="Recall" value={formatPercent(liveMetrics.recall)} tooltip={METRIC_TOOLTIPS.recall} />
          <MetricCard
            label="Passed"
            value={`${liveMetrics.passedCases}/${liveMetrics.totalCases}`}
          />
          <MetricCard label="Completed" value={`${completedCount}/${totalCases}`} />
        </div>
      )}

      <div className="overflow-x-auto max-h-[55vh] overflow-y-auto border border-brand-border rounded-lg shadow">
        <table className="min-w-full bg-brand-card">
          <thead className="sticky top-0 z-10">
            <tr>
              <TH>Status</TH>
              <TH>#</TH>
              <TH>Situation</TH>
              <TH>Segment</TH>
              <TH>Expected Suggestion</TH>
              <TH>Actual Suggestion</TH>
              <TH>Total Messages</TH>
              <TH>Result</TH>
              <TH>{""}</TH>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border">
            {cases.map((c, index) => {
              const status = caseStatuses.get(c.id) ?? "pending";
              const msgCount = caseMessageCounts.get(c.id) ?? 0;
              const result = resultsByCaseId.get(c.id);
              const sessionId = result?.sessionId || caseSessionIds.get(c.id);
              const isClickable = !!sessionId;
              return (
                <tr
                  key={c.id}
                  onClick={() => isClickable && onViewConversation(sessionId!)}
                  className={`transition-colors ${isClickable ? "hover:bg-brand-primary/5 cursor-pointer" : ""}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusIcon status={status} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-brand-foreground">
                    {c.id < 0 ? index + 1 : c.id}
                  </td>
                  <td className="px-4 py-3 text-sm text-brand-foreground max-w-[180px] truncate">
                    {c.modifiedCase.situation}
                  </td>
                  <td className="px-4 py-3 text-sm text-brand-muted max-w-[140px] truncate">
                    {c.modifiedCase.segment || "\u2014"}
                  </td>
                  <td className="px-4 py-3 text-sm text-brand-muted max-w-[160px] truncate">
                    {c.modifiedCase.expectedSuggestion || "None"}
                  </td>
                  <td className="px-4 py-3 text-sm text-brand-muted max-w-[160px] truncate">
                    {result
                      ? (result.actualSuggestions.length ? result.actualSuggestions.join(", ") : "None")
                      : "\u2014"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-brand-muted">
                    <span className="inline-flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      {msgCount}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {result ? <ResultBadge result={result} /> : null}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      {isCaseModified(c) && <ChangesWarning />}
                      <ViewConfigButton onClick={(e) => { e.stopPropagation(); setModalCaseId(c.id); }} />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalCase && (
        <CaseDetailModal
          benchmarkCase={modalCase}
          result={resultsByCaseId.get(modalCase.id)}
          status={caseStatuses.get(modalCase.id) ?? "pending"}
          messageCount={caseMessageCounts.get(modalCase.id) ?? 0}
          onClose={() => setModalCaseId(null)}
        />
      )}
    </div>
  );
}
